import { type BookkeepingLine, formatMinor, type LastTime } from '@hvac/contracts';
import { Button, Card, CardBody, CardHeader } from '@hvac/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { lastTimeQuery } from '../../shared/api/bookkeeping-queries.js';

/**
 * The same as last time.
 *
 * Most receipts from a supplier are coded the way the previous one was. The
 * last signed record is shown with its date, amount and coding, and how many
 * of the supplier's records were coded the same way, so the person can judge
 * whether this one is the usual before taking it. Taking it fills the form;
 * nothing is saved until they save.
 */

type TemplateLine = LastTime['lines'][number];

/**
 * Pairs this record's lines with the template's, largest with largest. A
 * record with more lines than the template gives the extra ones the
 * template's largest line, which is where a supplier's spending usually sits.
 */
export function pairLinesWithTemplate(
  lines: readonly BookkeepingLine[],
  template: readonly TemplateLine[],
): (readonly [BookkeepingLine, TemplateLine])[] {
  const first = template[0];
  if (first === undefined) return [];
  const ordered = [...lines].sort((a, b) => {
    const diff = BigInt(b.netAmountMinor) - BigInt(a.netAmountMinor);
    return diff > 0n ? 1 : diff < 0n ? -1 : a.lineNumber - b.lineNumber;
  });
  return ordered.map((line, index) => [line, template[index] ?? first] as const);
}

export function LastTimeCard({
  organizationId,
  recordId,
  onApply,
}: {
  organizationId: string;
  recordId: string;
  onApply: (template: LastTime) => void;
}) {
  const last = useQuery(lastTimeQuery(organizationId, recordId));
  if (last.data === undefined || last.data === null) return null;
  const template = last.data;
  const accounts = [...new Set(template.lines.map((line) => line.categoryName))].join(', ');
  const consistent = template.of > 1 && template.codedTheSame === template.of;
  return (
    <Card>
      <CardHeader
        title="The same as last time?"
        description={
          template.of <= 1
            ? 'The one signed record from this supplier.'
            : consistent
              ? `Every one of this supplier's ${String(template.of)} signed records was coded this way.`
              : `${String(template.codedTheSame)} of this supplier's ${String(template.of)} signed records were coded this way.`
        }
        actions={
          <Button
            size="sm"
            variant={consistent ? 'primary' : 'secondary'}
            onClick={() => onApply(template)}
          >
            Code it the same
          </Button>
        }
      />
      <CardBody className="text-sm">
        <p>
          <Link
            to="/bookkeeping/$recordId"
            params={{ recordId: template.transactionId }}
            className="text-brand-600 hover:underline"
          >
            {template.transactionDate}, {formatMinor(template.grossAmountMinor)}
          </Link>
          <span className="text-content-muted"> went to {accounts}.</span>
        </p>
        <p className="mt-1 text-xs text-content-muted">
          Fills the form with that coding. Nothing is saved until you save.
        </p>
      </CardBody>
    </Card>
  );
}
